import React, { useEffect, useState } from 'react';

import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { useQueryClient } from '@tanstack/react-query';
import FunctionDropdown from 'components/ObjectiveSidePanel/components/FunctionsDropdown';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import Task1 from 'models/task1';
import { useDispatch, useSelector } from 'react-redux';
import { findObject } from 'screens/admin/FunctionAndGrade/Helpers';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import styled from 'styled-components';
import { Button, ButtonSpacer, StyledDrawer } from 'styles/admin';
import { Divider1, NewStyledIconButton, StyledBox, StyledGrid, StyledTooltip } from 'styles/common';
import { checkOKRViewIsEnabled } from 'utils/helpers/featureFlags';

import { StringModifier, modifyString } from 'constants/copyHelpers';
import { findAndReplaceString, generateKey } from 'constants/helper';
import { StyledIcon } from 'constants/icons';
import { sendCreatedObjectiveEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { saveObjective } from 'services/objective';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';

import { Typography11, Typography12, Typography13 } from 'uiToolkit/Typography';

import ObjectiveAlignment from './components/ObjectiveAlignment';
import CollaboratorField from './components/ObjectiveCollaborators';
import ObjectiveDescription from './components/ObjectiveDescription';
import ObjectiveLevel from './components/ObjectiveLevel';
import ObjectiveName from './components/ObjectiveName';
import OwnerField from './components/ObjectiveOwners';
import ObjectiveTimeline from './components/ObjectiveTimeline';
import { ObjectiveSidePanelProvider, useObjectiveSidepanel } from './context/ObjectiveSidePanelContext';
import { findObjectiveTableObject, generateObjectiveObjectTemplate, insertObjectiveInObjectiveTable } from './helpers';

const HeaderWrapper = styled.div`
  padding: 0 1.5rem;
  display: flex;
  justify-content: space-between;
`;

const Box = styled(StyledBox)`
  background: white;
`;

const Header = ({ closeClickHandler, taskDetails, updateTask, isAlignmentAtParentLevel }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  return (
    <HeaderWrapper>
      <div style={{ display: 'flex', 'align-items': 'center' }}>
        <Typography11>Add {objectivesName}</Typography11>
      </div>
      <NewStyledIconButton onClick={closeClickHandler} circular padding="big" topGutter={1.5} bottomGutter={1.5}>
        <StyledIcon color="textSecondary" size="header">
          <CloseRoundedIcon />
        </StyledIcon>
      </NewStyledIconButton>
    </HeaderWrapper>
  );
};

const Footer = ({
  onSave,
  setIsOpenCreateGoalPanel = () => {},
  setTimeboundDetailsOfCreatedObjective = () => {},
  onCancel,
}) => {
  const { state } = useObjectiveSidepanel();
  const dispatch = useDispatch();

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const finalErrorText = findAndReplaceString(
    state.errorText,
    modifyString(objectivesName, StringModifier.SENTENCE_CASE),
    'objective'
  );

  console.log(state.errorText, 'state.errorTextstate.errorText');
  return (
    <Box padding="1rem" marginTop="auto" flexcontainer alignitems="center" justifycontent="flex-end">
      <StyledBox
        flexcontainer
        justifycontent="center"
        alignitems="center"
        style={{ minHeight: '2.5rem', width: '100%' }}
      >
        <Typography13 onClick={onCancel} style={{ width: '8rem', marginRight: 'auto' }} weight={600}>
          Cancel
        </Typography13>
        <StyledTooltip title={finalErrorText}>
          <span>
            <Button
              size="large"
              blueOutline
              rightGutter={3}
              onClick={() => {
                if (state.canSave) {
                  onSave({
                    tag: state,
                    onSuccess: (res) => {
                      setTimeboundDetailsOfCreatedObjective(res);
                      setIsOpenCreateGoalPanel(true);
                    },
                  });
                }
              }}
              style={{ width: '11rem', padding: 0, fontWeight: '600' }}
              disabled={!state?.canSave}
            >
              {`Save & Add ${keyResultsName}`}
            </Button>
          </span>
        </StyledTooltip>
        <ButtonSpacer />
        <StyledTooltip title={finalErrorText}>
          <span>
            <Button
              size="large"
              blue
              rightGutter={3}
              onClick={() => {
                if (state.canSave) {
                  onSave({
                    tag: state,
                    onSuccess: () => {
                      dispatch(showSuccessSnackbar('Objective successfully added!'));
                    },
                  });
                }
              }}
              style={{ width: '6rem', padding: 0, fontWeight: '600' }}
              disabled={!state?.canSave}
            >
              Save & Close
            </Button>
          </span>
        </StyledTooltip>
      </StyledBox>
    </Box>
  );
};

const ObjectiveSidePanelBody = ({
  onCancel,
  addObjective,
  parentUuid,
  otbPostCardId,
  selectedTimeBoundPeriod,
  objectiveTimeBoundId,
  isAlignmentAtParentLevel,
  setIsOpenCreateGoalPanel,
  setTimeboundDetailsOfCreatedObjective,
}) => {
  const { setParentTag, state, setOwnersCollaborators, functions, setDepartmentDetails } = useObjectiveSidepanel();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);
  // const { isEditable, functions, objectiveDetails, updateDepartmentDetails } =
  //   useObjectiveDetailsSidepanel();
  const [isFunctionDropDownOpen, setisFunctionDropDownOpen] = useState(false);

  useEffect(() => {
    if (parentUuid && objectiveTimeBoundId && otbPostCardId) {
      setParentTag(parentUuid, objectiveTimeBoundId, otbPostCardId);
    }
  }, [parentUuid, otbPostCardId, objectiveTimeBoundId]);

  useEffect(() => {
    setOwnersCollaborators([{ ...loggedInEmployee, ownerType: Task1.EMPLOYEE_TYPES.owner }]);
  }, [setOwnersCollaborators, loggedInEmployee]);

  /* Disabling My department function dropdown as per requirement raised by Mandloi */
  const showDepartmentDropdown = checkOKRViewIsEnabled() && state.objectiveLevel === 'DEPARTMENT';

  return (
    <>
      <Header isAlignmentAtParentLevel={isAlignmentAtParentLevel} closeClickHandler={onCancel} />
      <Divider1 />
      <ObjectiveFieldsContainer>
        <ObjectiveName />
        <ObjectiveAlignment
          isAlignmentAtParentLevel={isAlignmentAtParentLevel}
          objectiveTimeBoundId={objectiveTimeBoundId}
          selectedTimeBoundPeriod={selectedTimeBoundPeriod}
          parentUuid={parentUuid}
        />

        <StyledGrid container>
          <StyledGrid style={{ paddingRight: '1rem' }} item xs={6}>
            <ObjectiveLevel />
          </StyledGrid>
          {showDepartmentDropdown && (
            <StyledGrid xs={6} item style={{ paddingLeft: '1rem', marginTop: '0.5rem', width: ' 100%' }}>
              <Typography12 color="#757575" weight={600}>
                Department
              </Typography12>

              <FunctionDropdown
                setSelectedFunction={(data) => {
                  const selectedFunction = findObject(functions ?? [], data);
                  setDepartmentDetails(selectedFunction?.uuid, selectedFunction?.name);
                }}
                selectedFunction={state?.departmentId}
                functions={functions}
                isOpen={isFunctionDropDownOpen}
                setIsOpen={setisFunctionDropDownOpen}
                // isDropdownOnly
                placeholder=""
                isRowClickable={false}
              />
            </StyledGrid>
          )}
          <StyledGrid
            style={{
              paddingLeft: showDepartmentDropdown ? 0 : '1rem',
              marginTop: showDepartmentDropdown ? '1rem' : 0,
            }}
            item
            xs={showDepartmentDropdown ? 12 : 6}
          >
            <ObjectiveTimeline
              disabled={!isAlignmentAtParentLevel || Boolean(state?.parentTag?.id)}
              selectedTimeBoundPeriod={selectedTimeBoundPeriod}
            />
          </StyledGrid>
        </StyledGrid>

        <StyledGrid container alignItems="center" topGutter={3}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography12>Owner(s)</Typography12>
            <OwnerField
              currentOwnersCollaborators={state.ownersCollaborators}
              updateOwnersCollaborators={setOwnersCollaborators}
            />
          </div>
          <div style={{ width: '40px' }} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <Typography12>Collaborator(s)</Typography12>
            <CollaboratorField
              currentOwnersCollaborators={state.ownersCollaborators}
              updateOwnersCollaborators={setOwnersCollaborators}
            />
          </div>
        </StyledGrid>

        <ObjectiveDescription />
      </ObjectiveFieldsContainer>

      <Footer
        onSave={addObjective}
        setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
        setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
        onCancel={onCancel}
      />
    </>
  );
};

const ObjectiveSidePanel = ({
  open = false,
  onCancel = () => {},
  uuid,
  selectedTimeBoundPeriod,
  objectiveTimeBoundId,
  otbPostCardId,
  addNewObjective = () => {},
  rows = [],
  updateRowDataModifiedForArray,
  isAlignmentAtParentLevel,
  setIsOpenCreateGoalPanel,
  setTimeboundDetailsOfCreatedObjective,
  setNoData = () => {},
  source = '',
}) => {
  const dispatch = useDispatch();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const queryClient = useQueryClient();

  const addObjective = async ({ tag, onSuccess = () => {} }) => {
    try {
      const uiKey = generateKey(7);
      const parentPost = tag?.otbPostCardId ? { uuid: tag?.otbPostCardId } : null;

      const { objectiveTimeBound, id: newlyCreatedObjectivePostCardId } = await saveObjective({
        tag,
        uiKey,
        toIds: tag.ownersCollaborators,
        parentPost,
      });
      sendCreatedObjectiveEvent({
        eventProperties: {
          source,
          timePeriod: tag.timeBoundDetails.timelineTag,
          level: tag.objectiveLevel,
          description: tag.description,
          objectiveId: objectiveTimeBound?.id,
          type: tag?.parentTag?.id ? 'Sub-objective' : 'Objective',
        },
      });
      onCancel();

      const parentObjective = findObjectiveTableObject(rows, tag?.objectiveTimeBoundId);
      const { level } = parentObjective ?? {};

      const objectiveToBeAdded = generateObjectiveObjectTemplate({
        level: level ?? -1,
        objectiveTimeBoundId: objectiveTimeBound?.objectiveTimeBoundId,
        name: objectiveTimeBound?.name,
        postCardId: newlyCreatedObjectivePostCardId,
      });

      const newObjectivesArray = insertObjectiveInObjectiveTable(rows, tag?.objectiveTimeBoundId, objectiveToBeAdded);

      if (
        selectedTimeBoundPeriod?.timelineTag === objectiveTimeBound?.timeBoundDetails?.timelineTag &&
        selectedTimeBoundPeriod?.startTS === objectiveTimeBound?.timeBoundDetails?.startTS &&
        selectedTimeBoundPeriod?.endTS === objectiveTimeBound?.timeBoundDetails?.endTS
      ) {
        if (objectiveToBeAdded.level) {
          // if new objective to be added has level >0
          // that means it's a children objective
          addNewObjective(newObjectivesArray);
        } else {
          // if it's 0, that means it's a root level objective
          addNewObjective([objectiveToBeAdded, ...rows]);
        }
      }

      onSuccess({ ...objectiveTimeBound, postCardId: newlyCreatedObjectivePostCardId });

      queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);

      if (setNoData && typeof setNoData === 'function') {
        setNoData(false);
      }
    } catch (err) {
      console.error('Error in adding objective : ', err);
      // An objective with the same name already exists in the selected time frame.
      const updatedMsg = `${modifyString(objectivesName, StringModifier.UPPERCASE_ARTICLE)} ${modifyString(
        objectivesName,
        StringModifier.SENTENCE_CASE
      )} with the same name already exists in the selected time frame.`;
      dispatch(showErrorSnackbar(updatedMsg));
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        onCancel();
      }}
      ModalProps={{
        onBackdropClick: () => {
          onCancel();
        },
      }}
    >
      <ObjectiveSidePanelProvider>
        <ObjectiveSidePanelBody
          selectedTimeBoundPeriod={selectedTimeBoundPeriod}
          parentUuid={uuid}
          otbPostCardId={otbPostCardId}
          onCancel={onCancel}
          addObjective={addObjective}
          objectiveTimeBoundId={objectiveTimeBoundId}
          isAlignmentAtParentLevel={isAlignmentAtParentLevel}
          setIsOpenCreateGoalPanel={setIsOpenCreateGoalPanel}
          setTimeboundDetailsOfCreatedObjective={setTimeboundDetailsOfCreatedObjective}
        />
      </ObjectiveSidePanelProvider>
    </Drawer>
  );
};

const Drawer = styled(StyledDrawer)`
  ${(props) => props.theme.breakpoints.up('sm')} {
    .MuiDrawer-paper {
      width: 40%;
      padding: 0;
    }
  }
`;

const ObjectiveFieldsContainer = styled.div`
  padding: 1rem 1.5rem;
`;

export default ObjectiveSidePanel;
